
import React, { useEffect } from 'react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-slate-100">
          <div>
            <h3 className="text-2xl font-black text-slate-900">Privacy Policy</h3>
            <p className="text-slate-500 text-sm font-bold mt-1">Effective Date: 1 January 2026</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-6 md:p-8 space-y-8 text-slate-600 leading-relaxed text-sm md:text-base">
          <p className="font-medium">
            Lookapp.Au ("we", "us", or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard the data you provide when using our web applications and services.
            By using Lookapp.Au services, you agree to the terms of this Privacy Policy.
          </p>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">1. Information We Collect</h4>
            <p className="mb-3">We may collect the following types of information:</p>
            
            <h5 className="font-bold text-slate-800 mb-2">A. Personal Information</h5>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500 mb-4">
              <li>Name, email address, phone number</li>
              <li>Billing information (for subscription payments)</li>
              <li>Account credentials (if you create a login for the web app)</li>
            </ul>

            <h5 className="font-bold text-slate-800 mb-2">B. Non-Personal Information</h5>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500 mb-4">
              <li>Device information, browser type, IP address</li>
              <li>Usage data and analytics for service improvement</li>
            </ul>
            <p>We collect this information directly from you or automatically through your use of our services.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">2. How We Use Your Information</h4>
            <p className="mb-3">We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Provide, maintain, and improve our services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Communicate important updates and support information</li>
              <li>Analyze usage trends to improve the user experience</li>
              <li>Ensure security and prevent fraud or misuse</li>
            </ul>
            <p className="mt-3">We do not sell or rent your personal information to third parties.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">3. Sharing Your Information</h4>
            <p className="mb-3">We may share your information:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>With service providers (payment processors, hosting providers) to deliver our services</li>
              <li>If required by law or to protect our rights</li>
              <li>During a business transfer (merger, sale, or acquisition)</li>
            </ul>
            <p className="mt-3">We ensure all third parties follow strict privacy and security requirements.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">4. Cookies and Tracking</h4>
            <p className="mb-3">Our web apps may use:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Cookies and similar technologies to improve user experience</li>
              <li>Analytics tools to monitor traffic and usage</li>
            </ul>
            <p className="mt-3">You can control cookies through your browser settings, but some features of our web apps may not function properly without them.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">5. Your Rights</h4>
            <p className="mb-3">Depending on your jurisdiction, you may have rights to:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>Access your personal information</li>
              <li>Correct or update your data</li>
              <li>Request deletion of your information</li>
              <li>Opt-out of marketing communications</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, please contact us at lookapp.au@gmail.com.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">6. Data Retention</h4>
            <p>We retain your personal information as long as necessary to provide our services and comply with legal obligations. Unused accounts may be deleted after a reasonable period of inactivity.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">7. Security</h4>
            <p>We implement reasonable technical and organizational measures to protect your information from unauthorized access, disclosure, or misuse. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">8. Children’s Privacy</h4>
            <p>Our services are not directed to children under 13 (or the applicable age in your country). We do not knowingly collect personal information from children. If we become aware of such information, we will delete it promptly.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">9. International Users</h4>
            <p>If you are accessing our services from outside our operating country, please be aware that your data may be processed in countries with different privacy laws. By using our services, you consent to such processing.</p>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">10. Changes to This Privacy Policy</h4>
            <ul className="list-disc pl-5 space-y-1 marker:text-blue-500">
              <li>We may update this Privacy Policy periodically.</li>
              <li>The updated version will be posted on our website with the effective date. Continued use of our services constitutes acceptance of any changes.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-slate-900 font-black text-lg mb-3">11. Contact Us</h4>
            <p>For questions about this Privacy Policy or your personal information:</p>
            <p className="font-bold mt-2">Email: lookapp.au@gmail.com</p>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="p-6 md:p-8 border-t border-slate-100 bg-slate-50 rounded-b-3xl flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
